import { useState, useMemo } from "react";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import { cn } from "@/lib/utils";

interface MonthPickerDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedMonth: Date;
  onSelect: (date: Date) => void;
}

const MONTH_NAMES = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

export const MonthPickerDrawer = ({
  open,
  onOpenChange,
  selectedMonth,
  onSelect,
}: MonthPickerDrawerProps) => {
  const now = new Date();
  const currentYear = now.getFullYear();

  // Generate year options: current year and 2 previous
  const years = useMemo(() => {
    const yrs: number[] = [];
    for (let y = currentYear; y >= currentYear - 2; y--) yrs.push(y);
    return yrs;
  }, [currentYear]);

  const [pickerYear, setPickerYear] = useState(selectedMonth.getFullYear());

  const isFuture = (year: number, month: number) => {
    return year > now.getFullYear() || (year === now.getFullYear() && month > now.getMonth());
  };

  const isCurrent = (year: number, month: number) => {
    return year === now.getFullYear() && month === now.getMonth();
  };

  const isSelected = (year: number, month: number) => {
    return year === selectedMonth.getFullYear() && month === selectedMonth.getMonth();
  };

  const handleSelect = (month: number) => {
    if (isFuture(pickerYear, month)) return;
    onSelect(new Date(pickerYear, month, 1));
    onOpenChange(false);
  };

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent className="pb-8">
        <DrawerHeader className="pb-2">
          <DrawerTitle className="text-center">Select Month</DrawerTitle>
        </DrawerHeader>

        {/* Year pills */}
        <div className="flex justify-center gap-2 px-4 mb-4">
          {years.map((y) => (
            <button
              key={y}
              onClick={() => setPickerYear(y)}
              className={cn(
                "px-4 py-2 rounded-full text-sm font-medium transition-colors",
                pickerYear === y
                  ? "bg-primary text-primary-foreground"
                  : "bg-muted text-muted-foreground hover:bg-accent"
              )}
            >
              {y}
            </button>
          ))}
        </div>

        {/* 3x4 month grid */}
        <div className="grid grid-cols-3 gap-2 px-6">
          {MONTH_NAMES.map((name, idx) => {
            const future = isFuture(pickerYear, idx);
            const selected = isSelected(pickerYear, idx);
            const current = isCurrent(pickerYear, idx);

            return (
              <button
                key={idx}
                disabled={future}
                onClick={() => handleSelect(idx)}
                className={cn(
                  "py-3 rounded-xl text-sm font-medium transition-colors",
                  future && "text-muted-foreground/30 cursor-not-allowed",
                  !future && !selected && "hover:bg-accent text-foreground",
                  selected && "bg-primary text-primary-foreground",
                  current && !selected && "ring-2 ring-primary/40"
                )}
              >
                {name}
              </button>
            );
          })}
        </div>
      </DrawerContent>
    </Drawer>
  );
};
